import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  OnboardingAnswers,
  ChecklistSteps,
  clearOnboardingTour,
  getActiveOnboardingTour,
  getOnboardingStatus,
  markChecklistStep,
  markOnboardingCompleted,
  markOnboardingDismissed,
  markOnboardingIntroComplete,
  resetOnboardingIntro,
  saveOnboarding,
  startOnboardingTour,
  updateOnboardingTourStep,
} from '@/lib/onboarding-client';

type ProjectRef = {
  id: string;
  name: string;
};

type AnswerKey = keyof OnboardingAnswers;

export type OnboardingStep =
  | 'welcome'
  | 'building_type'
  | 'occupation'
  | 'use_case'
  | 'heard_from'
  | 'confirmation';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const QUESTION_ORDER: AnswerKey[] = ['building_type', 'occupation', 'use_case', 'heard_from'];

const SLIDE_ORDER: OnboardingStep[] = ['welcome', ...QUESTION_ORDER, 'confirmation'];

function getNextStep(answers: OnboardingAnswers): OnboardingStep {
  for (const key of QUESTION_ORDER) {
    if (!answers[key]) return key;
  }
  return 'confirmation';
}

export function useOnboarding({
  userId,
  projects,
  projectId,
}: {
  userId?: string | null;
  projects?: ProjectRef[];
  projectId?: string;
}) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'dismissed' | 'completed'>('idle');
  const [answers, setAnswers] = useState<OnboardingAnswers>({
    occupation: null,
    building_type: null,
    use_case: null,
    heard_from: null,
  });
  const [introComplete, setIntroComplete] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [checklistSteps, setChecklistSteps] = useState<ChecklistSteps>({
    extensionInstalled: false,
    sdkCopied: false,
    dashboardExplored: false,
  });
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [flowStep, setFlowStep] = useState<OnboardingStep>('welcome');
  const [activeTour, setActiveTour] = useState<ReturnType<typeof getActiveOnboardingTour>>(null);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setStatus('loading');

    getOnboardingStatus(userId).then((result) => {
      if (!active) return;
      setAnswers(result.answers);
      setIntroComplete(result.introComplete);
      setDismissed(result.dismissed);
      setCompleted(result.completed);
      setChecklistSteps(result.checklistSteps);
      setFlowStep(result.introComplete ? 'confirmation' : 'welcome');
      setActiveTour(getActiveOnboardingTour(userId));
      setStatus(result.completed ? 'completed' : result.dismissed ? 'dismissed' : 'ready');
    });

    return () => {
      active = false;
    };
  }, [userId]);

  const targetProject = useMemo(() => {
    if (!projects?.length) return null;
    return projects.find((project) => project.name === 'customer-portal') ?? projects[0];
  }, [projects]);

  const shouldShowProjectsOnboarding =
    status === 'ready' && !dismissed && !completed && !introComplete && Boolean(projects?.length);

  const shouldShowGettingStartedWelcome =
    status === 'ready' && !dismissed && !completed && !introComplete && projects?.length === 0;

  const shouldRedirectToProjects =
    status === 'ready' && !dismissed && !completed && !introComplete && Boolean(projects?.length);

  const shouldShowProjectTour =
    status === 'ready' &&
    !dismissed &&
    !completed &&
    introComplete &&
    Boolean(projectId) &&
    activeTour?.projectId === projectId;

  const currentSlideIndex = SLIDE_ORDER.indexOf(flowStep);

  const persist = useCallback(async (nextAnswers: OnboardingAnswers) => {
    if (!userId) return false;
    setSaveState('saving');
    const result = await saveOnboarding({ user_id: userId, ...nextAnswers });
    setSaveState(result.ok ? 'saved' : 'error');
    return result.ok;
  }, [userId]);

  const answer = useCallback(async (key: AnswerKey, value: string) => {
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);
    setFlowStep(getNextStep(nextAnswers));
    await persist(nextAnswers);
  }, [answers, persist]);

  const dismiss = useCallback(() => {
    if (!userId) return;
    markOnboardingDismissed(userId);
    setDismissed(true);
    setActiveTour(null);
    setStatus('dismissed');
    persist(answers);
  }, [userId, answers, persist]);

  const startIntro = useCallback(() => {
    setFlowStep(getNextStep(answers) === 'confirmation' ? 'confirmation' : getNextStep(answers));
  }, [answers]);

  const goBack = useCallback(() => {
    const nextIndex = Math.max(0, currentSlideIndex - 1);
    setFlowStep(SLIDE_ORDER[nextIndex]);
  }, [currentSlideIndex]);

  const startTour = useCallback((projectIdToOpen: string) => {
    if (!userId) return;
    markOnboardingIntroComplete(userId);
    setActiveTour(startOnboardingTour(userId, projectIdToOpen));
    setIntroComplete(true);
    setCompleted(false);
    setStatus('ready');
  }, [userId]);

  const advanceTour = useCallback(() => {
    if (!userId || !activeTour) return;
    setActiveTour(updateOnboardingTourStep(userId, activeTour.step + 1));
  }, [activeTour, userId]);

  const setTourStep = useCallback((step: number) => {
    if (!userId || !activeTour) return;
    setActiveTour(updateOnboardingTourStep(userId, step));
  }, [activeTour, userId]);

  const completeTour = useCallback(() => {
    if (!userId) return;
    markOnboardingCompleted(userId);
    setActiveTour(null);
    setCompleted(true);
    setStatus('completed');
  }, [userId]);

  const markIntroComplete = useCallback(() => {
    if (!userId) return;
    markOnboardingIntroComplete(userId);
    clearOnboardingTour(userId);
    setIntroComplete(true);
    setActiveTour(null);
  }, [userId]);

  const resetIntro = useCallback(() => {
    if (!userId) return;
    resetOnboardingIntro(userId);
    clearOnboardingTour(userId);
    setIntroComplete(false);
    setFlowStep('welcome');
    setActiveTour(null);
  }, [userId]);

  const setChecklistStep = useCallback((step: keyof ChecklistSteps, value: boolean) => {
    if (!userId) return;
    const next: ChecklistSteps = { ...checklistSteps, [step]: value };
    setChecklistSteps(next);
    markChecklistStep(userId, step, value);
    // Mark introComplete when they explore the dashboard so the Projects page
    // slideshow and spotlight tour don't fire after leaving getting-started.
    if (value && step === 'dashboardExplored') {
      markOnboardingIntroComplete(userId);
      setIntroComplete(true);
    }
    const allDone = next.extensionInstalled && next.sdkCopied && next.dashboardExplored;
    if (allDone) {
      markOnboardingCompleted(userId);
      setCompleted(true);
      setStatus('completed');
    } else if (completed) {
      setCompleted(false);
      setStatus('ready');
    }
  }, [userId, checklistSteps, completed]);

  const completeChecklistStep = useCallback((step: keyof ChecklistSteps) => {
    setChecklistStep(step, true);
  }, [setChecklistStep]);

  return {
    status,
    answers,
    flowStep,
    currentSlideIndex,
    totalSlides: SLIDE_ORDER.length,
    saveState,
    targetProject,
    activeTour,
    shouldShowProjectsOnboarding,
    shouldShowGettingStartedWelcome,
    shouldRedirectToProjects,
    shouldShowProjectTour,
    setFlowStep,
    startIntro,
    goBack,
    answer,
    dismiss,
    startTour,
    advanceTour,
    setTourStep,
    completeTour,
    checklistSteps,
    completeChecklistStep,
    setChecklistStep,
    markIntroComplete,
    resetIntro,
  };
}
